import { MegaMenuItemData } from "@/common/contracts/mega-menu.contracts";
import { ROOM_TYPES } from "@/features/open-home/utils/room-type";

/** Not Strapi ids; negative so they can never collide with one (same as the Pro Buyer item). */
export const OPEN_HOME_MENU_ITEM_ID = -2026;
const OPEN_HOME_SEE_ALL_ROOMS_ID = -2028;
/** One id per room type, below the ones above. */
const OPEN_HOME_ROOM_TYPE_ID_BASE = -2030;

/** "Discover" (Ontdek) in the Strapi menu: the same submenu the Pro Buyer item joins. */
export const DISCOVER_SUBMENU_SLUG = "discover-more";
/** Third in Discover's list, after Collections and Styles (Thomas, 10-27). */
const DISCOVER_POSITION = 2;

export const OPEN_HOME_ROUTE = "/open-home";

/** The concept name is the same in every language. */
const LABEL = "Open Home";

type SiteLocale = "en" | "nl" | "de" | "fr" | "es" | "it";

type MenuCopy = {
  /** The pink pill after "Open Home" (design: "Nieuw"-pil). */
  newBadge: string;
  seeAllRooms: string;
  types: Record<(typeof ROOM_TYPES)[number], string>;
};

/**
 * The header menu is built from Strapi at build time and loads no Open Home namespace, so this
 * copy lives in code, like the Pro Buyer item. The room names mirror the `oh-room-type-*` keys in
 * Lokalise (the chips on /open-home); a test keeps the two equal.
 */
export const OPEN_HOME_MENU_COPY: Record<SiteLocale, MenuCopy> = {
  en: {
    newBadge: "New",
    seeAllRooms: "See all rooms",
    types: {
      living: "Living room",
      bedroom: "Bedroom",
      dining: "Dining room",
      study: "Study",
      hallway: "Hallway",
      outdoor: "Garden and balcony",
    },
  },
  nl: {
    newBadge: "Nieuw",
    seeAllRooms: "Bekijk alle kamers",
    types: {
      living: "Woonkamer",
      bedroom: "Slaapkamer",
      dining: "Eetkamer",
      study: "Werkkamer",
      hallway: "Hal",
      outdoor: "Tuin en balkon",
    },
  },
  de: {
    newBadge: "Neu",
    seeAllRooms: "Alle Räume anzeigen",
    types: {
      living: "Wohnzimmer",
      bedroom: "Schlafzimmer",
      dining: "Esszimmer",
      study: "Arbeitszimmer",
      hallway: "Flur",
      outdoor: "Garten und Balkon",
    },
  },
  fr: {
    newBadge: "Nouveau",
    seeAllRooms: "Voir toutes les pièces",
    types: {
      living: "Séjour",
      bedroom: "Chambre",
      dining: "Salle à manger",
      study: "Bureau",
      hallway: "Entrée",
      outdoor: "Jardin et balcon",
    },
  },
  es: {
    newBadge: "Nuevo",
    seeAllRooms: "Ver todas las habitaciones",
    types: {
      living: "Salón",
      bedroom: "Dormitorio",
      dining: "Comedor",
      study: "Despacho",
      hallway: "Recibidor",
      outdoor: "Jardín y balcón",
    },
  },
  it: {
    newBadge: "Nuovo",
    seeAllRooms: "Vedi tutte le stanze",
    types: {
      living: "Soggiorno",
      bedroom: "Camera da letto",
      dining: "Sala da pranzo",
      study: "Studio",
      hallway: "Ingresso",
      outdoor: "Giardino e balcone",
    },
  },
};

const isSiteLocale = (locale: string | undefined): locale is SiteLocale =>
  !!locale && locale in OPEN_HOME_MENU_COPY;

const pathOf = (href?: string | null) =>
  (href?.split(/[?#]/)[0] ?? "").replace(/\/$/, "");

const hasOpenHomeItem = (items: MegaMenuItemData[]): boolean =>
  items.some(
    item =>
      item.id === OPEN_HOME_MENU_ITEM_ID ||
      pathOf(item.href) === OPEN_HOME_ROUTE ||
      hasOpenHomeItem(item.items ?? []) ||
      hasOpenHomeItem(item.submenu?.items ?? [])
  );

/**
 * "Open Home" with one link per room type; opens the room chips on /open-home. Clicking "Open
 * Home" itself opens /open-home too (Thomas, 10-07: "it does not do anything").
 */
const openHomeGroup = (copy: MenuCopy): MegaMenuItemData => ({
  id: OPEN_HOME_MENU_ITEM_ID,
  label: LABEL,
  badge: copy.newBadge,
  href: OPEN_HOME_ROUTE,
  opensOnClick: true,
  items: [
    {
      id: OPEN_HOME_SEE_ALL_ROOMS_ID,
      label: copy.seeAllRooms,
      href: OPEN_HOME_ROUTE,
      type: "links_title",
    },
    ...ROOM_TYPES.map((type, index) => ({
      id: OPEN_HOME_ROOM_TYPE_ID_BASE - index,
      label: copy.types[type],
      href: `${OPEN_HOME_ROUTE}?type=${type}`,
    })),
  ],
});

/**
 * Open Home sits in "Discover" (Ontdek), third in its list, with its room types beside it, on
 * desktop and in the mobile menu (same data). It moved there from "New" on 10-27 (Thomas): "New"
 * is a plain link again. A visitor who is not logged in lands on the sign-up prompt. Added in code
 * rather than in Strapi so it follows the `open_home` gate. When a site has no "Discover", the
 * entry goes right after "Sale". Skipped when the menu already links to /open-home, so a Strapi
 * entry never shows twice. Idempotent.
 */
export const withOpenHomeMenuItem = (
  items: MegaMenuItemData[],
  locale: string | undefined
): MegaMenuItemData[] => {
  if (!isSiteLocale(locale) || hasOpenHomeItem(items)) {
    return items;
  }

  const copy = OPEN_HOME_MENU_COPY[locale];
  const discoverAt = items.findIndex(
    item => item.submenuSlug === DISCOVER_SUBMENU_SLUG && item.submenu
  );
  const discover = items[discoverAt];

  if (!discover?.submenu) {
    const sale = items.findIndex(item => item.href?.startsWith("/sale"));
    const at = sale >= 0 ? sale + 1 : items.length;
    const entry: MegaMenuItemData = {
      id: OPEN_HOME_MENU_ITEM_ID,
      label: LABEL,
      badge: copy.newBadge,
      href: OPEN_HOME_ROUTE,
    };

    return [...items.slice(0, at), entry, ...items.slice(at)];
  }

  const groups = discover.submenu.items ?? [];
  const withGroup: MegaMenuItemData = {
    ...discover,
    submenu: {
      ...discover.submenu,
      items: [
        ...groups.slice(0, DISCOVER_POSITION),
        openHomeGroup(copy),
        ...groups.slice(DISCOVER_POSITION),
      ],
    },
  };

  return [
    ...items.slice(0, discoverAt),
    withGroup,
    ...items.slice(discoverAt + 1),
  ];
};
